var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { Alert, Button, Card, CardContent, CardHeader, IconButton, Stack, Tooltip, Typography, } from "@mui/material";
import dayjs from "dayjs";
import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useRetrieveDivisionsBySeasonId } from "../api/DivisionApiProvider";
import { useRetrieveLeague } from "../api/LeagueApiProvider";
import { useRetrieveUserLeaguePermissions } from "../api/PermissionApiProvider";
import { useCancelSeason, useCompleteSeason, useDeleteSeason, useRetrieveSeason, } from "../api/SeasonApiProvider";
import { ErrorView } from "../nav/Error";
import { Forbidden } from "../nav/Forbidden";
import { Loading } from "../nav/Loading";
import { DeleteDialog } from "../utils/DeleteDialog";
import ErrorSnackbar from "../utils/ErrorSnackbar";
import SuccessSnackbar from "../utils/SuccessSnackbar";
import { useNumericParams } from "../utils/useNumericParams";
import { DivisionManagement } from "./DivisionManagement";
import { SeasonStatusChip } from "./SeasonStatusChip";
export var Season = function () {
    var _a;
    var id = useNumericParams().id;
    var navigate = useNavigate();
    var _b = __read(useState(false), 2), showDelete = _b[0], setShowDelete = _b[1];
    var _c = __read(useState(false), 2), deleting = _c[0], setDeleting = _c[1];
    var _d = __read(useState(false), 2), successSnackbarOpen = _d[0], setSuccessSnackbarOpen = _d[1];
    var _e = __read(useState(""), 2), successSnackbarMessage = _e[0], setSuccessSnackbarMessage = _e[1];
    var _f = __read(useState(false), 2), errorSnackbarOpen = _f[0], setErrorSnackbarOpen = _f[1];
    var _g = __read(useState(""), 2), errorSnackbarMessage = _g[0], setErrorSnackbarMessage = _g[1];
    var _h = useRetrieveSeason({ id: id }), season = _h.data, isLoading = _h.isLoading, error = _h.error;
    var _j = useRetrieveLeague({
        id: season === null || season === void 0 ? void 0 : season.league,
        enabled: !!(season === null || season === void 0 ? void 0 : season.league),
    }), league = _j.data, leagueLoading = _j.isLoading;
    var _k = useRetrieveDivisionsBySeasonId({
        seasonId: season === null || season === void 0 ? void 0 : season.id,
        enabled: !!(season === null || season === void 0 ? void 0 : season.id),
    }), divisions = _k.data, divisionsLoading = _k.isLoading;
    var _l = useRetrieveUserLeaguePermissions({
        leagueId: season === null || season === void 0 ? void 0 : season.league,
    }), permissions = _l.data, permissionsLoading = _l.isLoading;
    var deleteMutation = useDeleteSeason({
        onMutate: function () { return setDeleting(true); },
        onSuccess: function () { return navigate("/leagues/".concat(season === null || season === void 0 ? void 0 : season.league, "/")); },
        onError: function () { return setDeleting(false); },
    });
    var completeMutation = useCompleteSeason();
    var cancelMutation = useCancelSeason();
    var canModifyStructure = function (status) {
        return status === "draft";
    };
    var handleStatusTransition = function (action) {
        if (!season)
            return;
        var mutations = {
            complete: completeMutation,
            cancel: cancelMutation,
            active: function () {
                // TODO: Implement season activation logic
                // This could involve:
                // 1. Creating fixtures/schedule
                // 2. Setting season status to active
                // 3. Notifying participants
                console.log("Starting season - implementation needed");
            },
        };
        var mutation = mutations[action];
        if (typeof mutation === "function") {
            mutation();
        }
        else {
            mutation === null || mutation === void 0 ? void 0 : mutation.mutate({ id: season.id });
        }
    };
    // Status state machine for determining available actions
    var getStatusTransitions = function (currentStatus) {
        var transitions = {
            draft: ["active"], // Draft seasons can only be started (if ready)
            active: ["complete", "cancel"], // Active seasons can be completed or cancelled
            completed: [], // No transitions from completed
            cancelled: [], // No transitions from cancelled
        };
        return transitions[currentStatus] || [];
    };
    var getAvailableActions = function () {
        if (!season || !(permissions === null || permissions === void 0 ? void 0 : permissions.canModify))
            return [];
        // Business rules for season readiness
        var isSeasonReady = function () {
            // Check if divisions data is available
            if (!divisions || divisions.length === 0) {
                return false; // No divisions exist
            }
            // Check that ALL divisions have at least 2 teams
            var allDivisionsValid = divisions.every(function (division) { return division.teams && division.teams.length >= 2; });
            if (!allDivisionsValid) {
                return false; // Some divisions don't have minimum 2 teams
            }
            // Check that season has a valid start date
            if (!season.startDate) {
                return false; // Season needs a start date
            }
            return true; // All checks passed
        };
        var availableTransitions = getStatusTransitions(season.status);
        var actionMap = {
            cancel: {
                key: "cancel",
                label: "Cancel Season",
                variant: "outlined",
                color: "error",
                tooltip: "Cancel this active season",
            },
            complete: {
                key: "complete",
                label: "Complete Season",
                variant: "contained",
                tooltip: "Mark this season as completed",
            },
            active: {
                key: "active",
                label: "Start Season",
                variant: "contained",
                color: "success",
                disabled: !isSeasonReady(),
                tooltip: isSeasonReady()
                    ? "Start this season and make it active"
                    : !divisions || divisions.length === 0
                        ? "Requires at least 1 division to start"
                        : !divisions.every(function (d) { return d.teams && d.teams.length >= 2; })
                            ? "All divisions need at least 2 teams to start"
                            : !season.startDate
                                ? "Season needs a start date to begin"
                                : "Season requirements not met",
            },
        };
        return availableTransitions
            .map(function (transition) { return actionMap[transition]; })
            .filter(function (action) {
            return Boolean(action);
        });
    };
    if (!id) {
        return _jsx(ErrorView, { error: new Error("Missing season id") });
    }
    // If retrieving the season is forbidden, show Forbidden
    if (((_a = error === null || error === void 0 ? void 0 : error.response) === null || _a === void 0 ? void 0 : _a.status) === 403) {
        return _jsx(Forbidden, {});
    }
    if (isLoading ||
        deleting ||
        permissionsLoading ||
        leagueLoading ||
        divisionsLoading) {
        return _jsx(Loading, {});
    }
    if (error || !season) {
        return _jsx(ErrorView, { error: error || new Error("Failed to load season") });
    }
    return (_jsxs(React.Fragment, { children: [_jsx(SuccessSnackbar, { open: successSnackbarOpen, message: successSnackbarMessage, onClose: function () {
                    setSuccessSnackbarOpen(false);
                    setSuccessSnackbarMessage("");
                } }), _jsx(ErrorSnackbar, { open: deleteMutation.isError || errorSnackbarOpen, message: deleteMutation.isError
                    ? "Failed to delete season. Please try again."
                    : errorSnackbarMessage, onClose: function () {
                    setErrorSnackbarOpen(false);
                    setErrorSnackbarMessage("");
                } }), _jsx(DeleteDialog, { open: showDelete, title: "Delete ".concat(season.name, "?"), onCancel: function () { return setShowDelete(false); }, onConfirm: function () {
                    return deleteMutation.mutate({
                        id: season.id,
                        leagueId: season.league,
                    });
                } }), season.status === "draft" && (_jsx(Alert, { severity: "info", sx: { mb: 2 }, children: "This season is in draft mode. You can freely add divisions and teams." })), _jsxs(Card, { children: [_jsx(CardHeader, { title: _jsxs(Stack, { spacing: 1, children: [league && (_jsx(Link, { to: "/leagues/".concat(season.league, "/"), style: { textDecoration: "none" }, children: _jsx(Typography, { variant: "body2", color: "primary.main", children: league.name }) })), _jsxs(Stack, { direction: "row", spacing: 2, alignItems: "center", children: [_jsx(Typography, { variant: "h5", children: season.name }), _jsx(SeasonStatusChip, { season: season })] })] }), subheader: season.startDate ? (_jsxs(Typography, { variant: "caption", fontStyle: "italic", children: ["Starts ", dayjs(season.startDate).format("MMM D, YYYY")] })) : undefined, action: _jsxs(Stack, { direction: "row", spacing: 1, children: [getAvailableActions().map(function (action) { return (_jsx(Tooltip, { title: action.tooltip || "", children: _jsx("span", { children: _jsx(Button, { variant: action.variant, color: action.color, size: "small", onClick: function () { return handleStatusTransition(action.key); }, disabled: action.disabled ||
                                                completeMutation.isLoading ||
                                                cancelMutation.isLoading, children: action.label }) }) }, action.key)); }), (permissions === null || permissions === void 0 ? void 0 : permissions.canModify) && canModifyStructure(season.status) && (_jsxs(_Fragment, { children: [_jsx(Link, { to: "/seasons/".concat(season.id, "/edit/"), children: _jsx(IconButton, { "aria-label": "edit", children: _jsx(EditIcon, {}) }) }), _jsx(IconButton, { "aria-label": "delete", onClick: function () { return setShowDelete(true); }, children: _jsx(DeleteIcon, {}) })] }))] }) }), season.description && (_jsx(CardContent, { children: _jsx(Typography, { variant: "body2", color: "text.secondary", children: season.description }) }))] }), season.status === "draft" && (_jsx(DivisionManagement, { season: season, canModify: (permissions === null || permissions === void 0 ? void 0 : permissions.canModify) || false, onDeleteSuccess: function (message) {
                    setSuccessSnackbarMessage(message);
                    setSuccessSnackbarOpen(true);
                }, onDeleteError: function (message) {
                    setErrorSnackbarMessage(message);
                    setErrorSnackbarOpen(true);
                } }))] }));
};
